import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { startOfMonth, endOfMonth, subMonths } from 'date-fns'
import FilterCalendar from '../../calendar/FilterCalendar'
import DeleteButton from '../../buttons/actions-button/DeleteButton'
import EditButton from '../../buttons/actions-button/EditButton'
import DownloadComponent from '../../../pages/documents/accounts/DownloadComponent'
import {
  Invoice,
  fetchInvoices,
  fetchCurrentInvoices,
  setIsVisibleModalInvoice,
  setVisibleDeleteModalInvoice,
} from '../../../redux/reducers/invoices'
import { invoicesSelector } from '../../../redux/selectors/invoices-selector'
import { cargosSelector } from '../../../redux/selectors/cargos-selector'
import { AppDispatch } from '../../../redux/store'
import { formatDateToDDMMYYYY } from '../../../utils/date'
import { getAmount, getItemCurrency } from '../../../utils/getAmount'
import { getRole } from '../../../utils/getRole'
import { useOutsideClick } from '../../../hooks/useOutsideClick'
import accountsStyles from '../../../pages/documents/accounts/Accounts.module.css'
import styles from './AddClientModal.module.css'

const STATUSES = [
  { id: null as number | null, name: 'Все статусы' },
  { id: 1, name: 'Оплачены' },
  { id: 0, name: 'Не оплачены' },
  { id: 2, name: 'На оплате' },
]

const getDefaultDateRange = () => {
  const now = new Date()
  return {
    startDate: startOfMonth(subMonths(now, 1)),
    endDate: endOfMonth(now),
  }
}

const ClientInvoicesTab: React.FC<{ clientId: number }> = ({ clientId }) => {
  const dispatch = useDispatch<AppDispatch>()
  const { isClient } = getRole()
  const { invoices, isVisibleModalInvoice } = useSelector(invoicesSelector)
  const { isVisibleModalCargo } = useSelector(cargosSelector)

  const defaultRange = useMemo(() => getDefaultDateRange(), [])
  const [startDate, setStartDate] = useState<Date>(defaultRange.startDate)
  const [endDate, setEndDate] = useState<Date>(defaultRange.endDate)
  const [status, setStatus] = useState<{ id: number | null; name: string }>(
    STATUSES[0]
  )
  const [isStatusOpen, setIsStatusOpen] = useState(false)
  const statusRef = useOutsideClick(() => setIsStatusOpen(false))

  const invoiceModalWasOpen = useRef(false)
  const cargoModalWasOpen = useRef(false)

  const loadInvoices = useCallback(() => {
    dispatch(
      fetchInvoices({
        client: clientId.toString(),
        date_from: startDate.toLocaleDateString('en-CA'),
        date_to: endDate.toLocaleDateString('en-CA'),
      })
    )
  }, [clientId, dispatch, startDate, endDate])

  useEffect(() => {
    loadInvoices()
  }, [loadInvoices])

  useEffect(() => {
    if (invoiceModalWasOpen.current && !isVisibleModalInvoice) loadInvoices()
    invoiceModalWasOpen.current = isVisibleModalInvoice
  }, [isVisibleModalInvoice, loadInvoices])

  useEffect(() => {
    if (cargoModalWasOpen.current && !isVisibleModalCargo) loadInvoices()
    cargoModalWasOpen.current = isVisibleModalCargo
  }, [isVisibleModalCargo, loadInvoices])

  const clientInvoices = useMemo(
    () =>
      invoices.filter((inv) => {
        if (inv.client_id !== clientId) return false
        if (status.id === null) return true
        return Number(inv.paid) === status.id
      }),
    [invoices, clientId, status.id]
  )

  const handleEdit = useCallback(
    async (item: Invoice) => {
      try {
        await dispatch(fetchCurrentInvoices(item.id)).unwrap()
        dispatch(setIsVisibleModalInvoice(true))
      } catch {}
    },
    [dispatch]
  )

  const getPaidStatus = (paidStatus: number) => {
    switch (paidStatus) {
      case 1:
        return 'Оплачен'
      case 2:
        return 'На оплате'
      default:
        return 'Не оплачен'
    }
  }

  const getAmountBadgeClass = (paid: number) => {
    if (paid === 1) return accountsStyles.bgGreen
    if (paid === 2) return styles.bgTurquoise
    return accountsStyles.bgRed
  }

  return (
    <div>
      <div className={classNames(styles.filterForm)}>
        <div className="row align-items-end">
          <div className="col-6">
            <FilterCalendar
              startDate={startDate}
              endDate={endDate}
              isFixed={false}
              horizontalOnDesktop
              onDateChange={(start, end) => {
                setStartDate(start)
                setEndDate(end)
              }}
            />
          </div>
          <div className="col-6">
            <div
              ref={statusRef}
              className={classNames(accountsStyles.dropdown, 'd-block')}
              style={{ textAlign: 'left' }}
            >
              <div
                onClick={() => setIsStatusOpen((v) => !v)}
                className={classNames(
                  accountsStyles.btn,
                  accountsStyles.btnSmall,
                  accountsStyles.btnWide,
                  accountsStyles.btnInput,
                  accountsStyles.btnDropdown,
                  accountsStyles.nowrap,
                  isStatusOpen && accountsStyles.btnDropdownActive
                )}
              >
                {status.name}
              </div>
              <div
                className={classNames(
                  accountsStyles.dropdownContent,
                  accountsStyles.dropdownSelect,
                  isStatusOpen && accountsStyles.dropdownContentActive
                )}
              >
                {STATUSES.map((item) => (
                  <div
                    key={String(item.id)}
                    onClick={() => {
                      setStatus(item)
                      setIsStatusOpen(false)
                    }}
                  >
                    {item.name}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {!clientInvoices.length && (
        <div
          className={classNames(styles.small2, 'pt-2 pb-3')}
          style={{ textAlign: 'center' }}
        >
          <span className={styles.colorGray}>Нет счетов</span>
        </div>
      )}

      {clientInvoices.map((item) => (
        <div
          key={item.id}
          className={classNames(
            accountsStyles.bgWhite,
            accountsStyles.border10,
            accountsStyles.bxShadow,
            accountsStyles.docItem,
            accountsStyles.small2,
            'pl-2 pr-2 mb-3'
          )}
        >
          <div className={classNames(accountsStyles.row, 'row justify-content-end')}>
            <div
              onClick={() => handleEdit(item)}
              className={classNames(
                accountsStyles.colorGray,
                'order-1 col-4 col-sm-3 col-md-3 col-lg-3 col-xl-2 pt-1 pb-1'
              )}
              style={{ cursor: 'pointer' }}
            >
              <b>{item.id}</b>
              <br />
              <span>{formatDateToDDMMYYYY(item.date)}</span>
            </div>
            <div
              onClick={() => handleEdit(item)}
              className={classNames(
                accountsStyles.docStatus,
                'order-2 col-3 col-sm-3 col-md-3 col-lg-3 col-xl-2 pt-1 pb-1'
              )}
            >
              {String(item.paid) === '1' && (
                <i
                  className={classNames(
                    accountsStyles.icon,
                    accountsStyles.iconVerified2,
                    accountsStyles.icon16
                  )}
                />
              )}{' '}
              <span
                className={classNames(
                  accountsStyles.small3,
                  item.paid === 0 && accountsStyles.colorRed,
                  item.paid === 2 && accountsStyles.colorTurquoise
                )}
              >
                {getPaidStatus(item.paid)}
              </span>
              {Boolean(item?.signed) && (
                <div>
                  <i
                    className={classNames(
                      accountsStyles.icon,
                      accountsStyles.iconEditBlue,
                      accountsStyles.icon16
                    )}
                  />{' '}
                  <span className={accountsStyles.small3}>Подписан</span>
                </div>
              )}
            </div>
            <div
              onClick={() => handleEdit(item)}
              className="order-3 col-5 col-sm pt-1 pb-1"
              style={{ textAlign: 'right' }}
            >
              <span
                className={classNames(
                  getAmountBadgeClass(item.paid),
                  accountsStyles.colorWhite,
                  accountsStyles.borderRadius5,
                  'd-inline-block pt-1 pb-1 pl-2 pr-2'
                )}
              >
                <b>
                  {getAmount(item.sum)}{' '}
                  {getItemCurrency(item.client?.currency)}
                </b>
              </span>
            </div>
            <div
              className="order-5 order-sm-4 col-auto col-sm pt-1 pb-1"
              style={{ textAlign: 'right' }}
            >
              <EditButton
                onClick={() => handleEdit(item)}
                isSmallSize={true}
              />
              {(item?.pdf_url || item?.pdf_with_stamp_url) && (
                <DownloadComponent item={item} isSmallSize={true} />
              )}
              {!isClient && (
                <DeleteButton
                  isSmallSize={true}
                  onClick={() => {
                    dispatch(setVisibleDeleteModalInvoice(item))
                  }}
                />
              )}
            </div>
            {item?.note && (
              <div className="order-4 order-sm-5 col col-sm-12 pt-1 pb-1 pr-2">
                <div className={classNames(accountsStyles.small3, 'd-flex')}>
                  <i
                    className={classNames(
                      accountsStyles.icon,
                      accountsStyles.iconAlarm,
                      accountsStyles.icon16,
                      'mr-1'
                    )}
                  />
                  <span>{item.note}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default ClientInvoicesTab
